import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HubConnection } from '@microsoft/signalr';

import { IState } from '../reducers';
import { IConfigReducer } from '../reducers/configReducer';
import { IContextReducer } from '../reducers/contextReducer';
import { ISingleConversation, ISingleMessage } from '../entities/Messaging/IMessaging';
import { IClusterSingleMessage, IClusterValidationRequestStatusUpdateMessage, ISingleCluster } from '../entities/IClusters';
import { ensureSignalRConnection } from './signalRConnectionManager';
import { selectSignalREnabled } from './platformConfiguration';
import { shouldRefreshApprovalBadgeFromRows } from './approvalBadgeRefresh';

export interface IDataTableRowNotification {
    indexKey: string;
    rowId: string;
}

export interface IStatusEntityUpdatedMessage {
    additionalInformation?: {
        clusterId?: string;
    };
    clusterId?: string;
    id?: string;
}

const normalizeDatatableRowsPayload = (
    message: IDataTableRowNotification[] | IDataTableRowNotification | undefined
): IDataTableRowNotification[] => {
    const rows = Array.isArray(message)
        ? message
        : (message ? [message] : []);

    return rows.filter(item =>
        !!item &&
        typeof item.indexKey === 'string' &&
        item.indexKey.length > 0 &&
        typeof item.rowId === 'string' &&
        item.rowId.length > 0
    );
};

const mergeUniqueRows = (
    prev: IDataTableRowNotification[],
    nextRows: IDataTableRowNotification[]
): IDataTableRowNotification[] => {
    const all = [...prev, ...nextRows];
    const unique: IDataTableRowNotification[] = [];
    const seen = new Set<string>();

    for (const item of all) {
        const key = `${item.indexKey}:${item.rowId}`;
        if (!seen.has(key)) {
            seen.add(key);
            unique.push(item);
        }
    }

    return unique;
};

const useSignalREvent = <TPayload>(
    connection: HubConnection | undefined,
    eventName: string,
    handler: (payload: TPayload) => void
) => {
    useEffect(() => {
        if (!connection) {
            return;
        }

        const boundHandler = (payload: TPayload) => handler(payload);

        connection.on(eventName, boundHandler);

        return () => {
            connection.off(eventName, boundHandler);
        };
    }, [connection, eventName, handler]);
};

export const useNotification = () => {
    const { token, appConfig, signalREnabled } = useSelector<IState, IContextReducer & IConfigReducer & { signalREnabled: boolean }>(state => ({
        ...state.context,
        ...state.config,
        signalREnabled: selectSignalREnabled(state)
    }));
    const dispatch = useDispatch();
    const [connection, setConnection] = useState<HubConnection | undefined>(undefined);
    const [datatableRowIds, setDatatableRowIds] = useState<IDataTableRowNotification[]>([]);
    const [datatableCreatedRowIds, setDatatableCreatedRowIds] = useState<IDataTableRowNotification[]>([]);
    const [receiveChannelMessage, setReceiveChannelMessage] = useState<IClusterSingleMessage>(undefined);
    const [updateChannelMessage, setUpdateChannelMessage] = useState<IClusterSingleMessage>(undefined);
    const [assignedRoleCreated, setAssignedRoleCreated] = useState<IClusterSingleMessage>(undefined);
    const [deleteChannelMessage, setDeleteChannelMessage] = useState<IClusterSingleMessage>(undefined);
    const [channelMembersAdded, setChannelMembersAdded] = useState<any>(undefined);
    const [channelMemberRemoved, setChannelMemberRemoved] = useState<any>(undefined);
    const [messageWasRead, setMessageWasRead] = useState<any>(undefined);
    const [receiveNotification, setReceiveNotification] = useState<any>(undefined);
    const [conversationMemberRemoved, setConversationMemberRemoved] = useState<any>(undefined);
    const [assignToClusterCompleted, setAssignToClusterCompleted] = useState<ISingleCluster>(undefined);
    const [receiveConversation, setReceiveConversation] = useState<ISingleConversation>(undefined);
    const [attachmentUploaded, setAttachmentUploaded] = useState<ISingleMessage>(undefined);
    const [receiveMessage, setReceiveMessage] = useState<ISingleMessage>(undefined);
    const [clusterValidationRequestStatusUpdateMessageDto, setClusterValidationRequestStatusUpdateMessageDto] = useState<IClusterValidationRequestStatusUpdateMessage>(undefined);
    const [statusEntityUpdatedMessage, setStatusEntityUpdatedMessage] = useState<IStatusEntityUpdatedMessage>(undefined);
    const [recalculateMyValidationRequestsTick, setRecalculateMyValidationRequestsTick] = useState(0);

    useEffect(() => {
        if (!signalREnabled || !token || !appConfig.signalRUri) {
            return;
        }

        let isMounted = true;

        ensureSignalRConnection(dispatch, appConfig.signalRUri, token)
            .then(hub => {
                if (isMounted) {
                    setConnection(hub);
                }
            })
            .catch(error => {
                if (process.env.NODE_ENV === 'development') {
                    // eslint-disable-next-line no-console
                    console.error('SignalR connection initialization failed', error);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [dispatch, appConfig.signalRUri, token, signalREnabled]);

    const handleDataTableRowsUpdated = useCallback((message: IDataTableRowNotification[] | IDataTableRowNotification) => {
        const normalizedRows = normalizeDatatableRowsPayload(message);
        if (normalizedRows.length === 0) {
            return;
        }
        if (shouldRefreshApprovalBadgeFromRows(normalizedRows)) {
            setRecalculateMyValidationRequestsTick(t => t + 1);
        }
        setDatatableRowIds(prev => mergeUniqueRows(prev, normalizedRows));
    }, []);

    const handleDataTableRowCreated = useCallback((message: IDataTableRowNotification[] | IDataTableRowNotification) => {
        const normalizedRows = normalizeDatatableRowsPayload(message);
        if (normalizedRows.length === 0) {
            return;
        }
        if (shouldRefreshApprovalBadgeFromRows(normalizedRows)) {
            setRecalculateMyValidationRequestsTick(t => t + 1);
        }
        setDatatableCreatedRowIds(prev => mergeUniqueRows(prev, normalizedRows));
    }, []);

    const handleReceiveChannelMessage = useCallback((message: IClusterSingleMessage) => {
        setReceiveChannelMessage(message);
    }, []);

    const handleUpdateChannelMessage = useCallback((message: IClusterSingleMessage) => {
        setUpdateChannelMessage(message);
    }, []);

    const handleAssignedRoleCreated = useCallback((message: IClusterSingleMessage) => {
        setAssignedRoleCreated(message);
    }, []);

    const handleChannelMembersAdded = useCallback((message: any) => {
        setChannelMembersAdded(message);
    }, []);

    const handleChannelMemberRemoved = useCallback((message: any) => {
        setChannelMemberRemoved(message);
    }, []);

    const handleAssignToClusterCompleted = useCallback((message: ISingleCluster) => {
        setAssignToClusterCompleted(message);
    }, []);

    const handleDeleteChannelMessage = useCallback((message: IClusterSingleMessage) => {
        setDeleteChannelMessage(message);
    }, []);

    const handleMessageWasRead = useCallback((message: any) => {
        setMessageWasRead(message);
    }, []);

    const handleReceiveConversation = useCallback((message: ISingleConversation) => {
        setReceiveConversation(message);
    }, []);

    const handleAttachmentUploaded = useCallback((message: ISingleMessage) => {
        setAttachmentUploaded(message);
    }, []);

    const handleReceiveMessage = useCallback((message: ISingleMessage) => {
        setReceiveMessage(message);
    }, []);

    const handleReceiveNotification = useCallback((message: any) => {
        setReceiveNotification(message);
    }, []);

    const handleConversationMemberRemoved = useCallback((message: any) => {
        setConversationMemberRemoved(message);
    }, []);

    const handleClusterValidationRequestStatusUpdate = useCallback((message: IClusterValidationRequestStatusUpdateMessage) => {
        setClusterValidationRequestStatusUpdateMessageDto(message);
        setRecalculateMyValidationRequestsTick(t => t + 1);
    }, []);

    const handleRecalculateMyValidationRequests = useCallback(() => {
        setRecalculateMyValidationRequestsTick(t => t + 1);
    }, []);

    const handleStatusEntityUpdated = useCallback((message: IStatusEntityUpdatedMessage) => {
        const normalized: IStatusEntityUpdatedMessage =
            message !== null && typeof message === 'object'
                ? { ...message }
                : { id: message as unknown as string };
        setStatusEntityUpdatedMessage(normalized);
    }, []);

    useSignalREvent(connection, 'DataTableRowsUpdated', handleDataTableRowsUpdated);
    useSignalREvent(connection, 'DataTableRowCreated', handleDataTableRowCreated);
    useSignalREvent(connection, 'ReceiveChannelMessage', handleReceiveChannelMessage);
    useSignalREvent(connection, 'UpdateChannelMessage', handleUpdateChannelMessage);
    useSignalREvent(connection, 'AssignedRoleCreated', handleAssignedRoleCreated);
    useSignalREvent(connection, 'ChannelMembersAdded', handleChannelMembersAdded);
    useSignalREvent(connection, 'ChannelMemberRemoved', handleChannelMemberRemoved);
    useSignalREvent(connection, 'AssignToClusterCompleted', handleAssignToClusterCompleted);
    useSignalREvent(connection, 'DeleteChannelMessage', handleDeleteChannelMessage);
    useSignalREvent(connection, 'messageWasRead', handleMessageWasRead);
    useSignalREvent(connection, 'receiveConversation', handleReceiveConversation);
    useSignalREvent(connection, 'attachmentUploaded', handleAttachmentUploaded);
    useSignalREvent(connection, 'receiveMessage', handleReceiveMessage);
    useSignalREvent(connection, 'receiveNotification', handleReceiveNotification);
    useSignalREvent(connection, 'conversationMemberRemoved', handleConversationMemberRemoved);
    useSignalREvent(connection, 'ClusterValidationRequestStatusUpdateMessageDto', handleClusterValidationRequestStatusUpdate);
    useSignalREvent(connection, 'RecalculateMyValidationRequests', handleRecalculateMyValidationRequests);
    useSignalREvent(connection, 'StatusEntityUpdated', handleStatusEntityUpdated);

    return {
        receiveChannelMessage,
        updateChannelMessage,
        assignedRoleCreated,
        channelMembersAdded,
        channelMemberRemoved,
        assignToClusterCompleted,
        deleteChannelMessage,
        messageWasRead,
        receiveConversation,
        attachmentUploaded,
        receiveMessage,
        receiveNotification,
        conversationMemberRemoved,
        datatableRowIds,
        setDatatableRowIds,
        datatableCreatedRowIds,
        setDatatableCreatedRowIds,
        clusterValidationRequestStatusUpdateMessageDto,
        statusEntityUpdatedMessage,
        recalculateMyValidationRequestsTick
    };
};